import {
    TablePagination,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { customInstanceBpm } from "../../../api/axios-client";
import { useAuth } from "../../../auth";
import ListLayout from "../../../components/Layout/ListLayout";
import ProcessCards from "../../../components/Processes/ProcessCards";
import ProcessFilters, { ProcessFilterValues } from "../../../components/Processes/ProcessFilters";
import ProcessList from "../../../components/Processes/ProcessList";
import { buildProcessQueryParams, processStateFilterValues } from "../../../components/Processes/processQueryParams";
import { encodeNavParams } from "../../../utils/navParams";

const processesSearchSchema = z.object({
    page: z.number().catch(1),
    size: z.number().catch(10),
    q: z.string().optional(),
    state: z.enum(processStateFilterValues).optional(),
    startTimeFrom: z.string().optional(),
    startTimeTo: z.string().optional(),
    endTimeFrom: z.string().optional(),
    endTimeTo: z.string().optional(),
    incidentTimeFrom: z.string().optional(),
    incidentTimeTo: z.string().optional(),
    dashboardScope: z.boolean().optional(),
    category: z.string().optional(),
    processDefinitionId: z.string().optional(),
    processDefinitionKey: z.string().optional(),
    orderBy: z.string().catch('startTime'),
    order: z.enum(['asc', 'desc']).catch('desc'),
});

export const Route = createFileRoute('/__auth/processes/')({
    component: ProcessesPage,
    validateSearch: (search) => processesSearchSchema.parse(search),
    staticData: {
        title: "routes.processes",
    },
});

function ProcessesPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { user, currentTenant } = useAuth();

    const navigate = Route.useNavigate();
    const { t } = useTranslation();
    const search = Route.useSearch();

    const queryParams = useMemo(() => {
        return buildProcessQueryParams(search);
    }, [search]);

    const { data: processesBlob, isLoading, error } = useQuery({
        queryKey: ['process-instances', queryParams],
        queryFn: () => customInstanceBpm<any>(
            `/smart-bpm/rest/history/process-instance?${queryParams}`,
            { method: 'GET' }
        ),
        enabled: !!user?.id && !!currentTenant,
    });

    const data = useMemo(() => {
        const raw = processesBlob as any;
        if (!raw?.content) return raw;
        const seen = new Map<string, any>();
        for (const proc of raw.content) {
            if (proc.id && !seen.has(proc.id)) {
                seen.set(proc.id, proc);
            }
        }
        return { ...raw, content: Array.from(seen.values()) };
    }, [processesBlob]);

    const handlePageChange = (_: unknown, value: number) => {
        navigate({
            search: (prev) => ({ ...prev, page: value }),
            replace: true
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleProcessClick = (process: any) => {
        const fromParams = encodeNavParams({ ...search });
        navigate({
            to: '/processes/$processInstanceId/history/details',
            params: { processInstanceId: process.id },
            search: (() => ({ from: 'processes' as const, fromParams })) as any
        });
    };

    const handleHistoryClick = (process: any) => {
        const fromParams = encodeNavParams({ ...search });
        navigate({
            to: '/processes/$processInstanceId/history/details',
            params: { processInstanceId: process.id },
            search: (() => ({ from: 'processes' as const, fromParams })) as any
        });
    };

    const handleFilter = (newFilters: ProcessFilterValues) => {
        navigate({
            search: (prev) => ({
                ...prev,
                ...newFilters,
                page: 1
            }),
        });
    };

    const handleRequestSort = (property: string) => {
        const isAsc = search.orderBy === property && search.order === 'asc';
        navigate({
            search: (prev) => ({
                ...prev,
                orderBy: property,
                order: isAsc ? 'desc' : 'asc'
            })
        });
    };

    return (
        <ListLayout
            title={t("processes.title")}
            subtitle={t("processes.subtitle")}
            filters={
                <ProcessFilters
                    onFilter={handleFilter}
                    isLoading={isLoading}
                    initialValues={{
                        q: search.q,
                        state: search.state,
                        startTimeFrom: search.startTimeFrom,
                        startTimeTo: search.startTimeTo,
                        category: search.category
                    }}
                />
            }
            error={error}
            isLoading={isLoading}
            isEmpty={!data?.content?.length}
            emptyMessage={t("processes.emptyMessage")}
        >
            {isMobile ? (
                <ProcessCards
                    processes={data?.content || []}
                    onProcessClick={handleProcessClick}
                    onHistoryClick={handleHistoryClick}
                />
            ) : (
                <ProcessList
                    processes={data?.content || []}
                    onProcessClick={handleProcessClick}
                    onHistoryClick={handleHistoryClick}
                    orderBy={search.orderBy || 'startTime'}
                    order={search.order || 'desc'}
                    onRequestSort={handleRequestSort}
                />
            )}

            <TablePagination
                component="div"
                count={data?.totalElements || 0}
                page={(search.page || 1) - 1}
                onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
                rowsPerPage={search.size || 10}
                onRowsPerPageChange={(e) => {
                    const newSize = parseInt(e.target.value, 10);
                    navigate({
                        search: (prev) => ({ ...prev, size: newSize, page: 1 }),
                        replace: true
                    });
                }}
                labelRowsPerPage={t("common.rowsPerPage")}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                    mt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    mb: 8
                }}
            />
        </ListLayout>
    );
}
